import { useState, useCallback, useEffect } from 'react';
import { ethers } from 'ethers';
import { usePublicClient, useWalletClient } from 'wagmi';
import { 
  getProvider, 
  checkAllowance, 
  checkAllowanceWagmi,
  approveToken, 
  approveTokenWagmi,
  bridgeToken, 
  bridgeTokenWagmi,
  estimateBridgeFee,
  getTokenBalance,
  getTokenBalanceWagmi,
  checkTransactionStatus,
  checkCrossChainMessageStatus
} from '../utils/web3';
import { CHAIN_CONFIGS, LOCAL_STORAGE_KEY } from '../utils/constants';
import { TokenType, BridgeTransaction, TransactionState } from '../types';

export const useBridge = (walletAddress: string) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  
  const [fromChain, setFromChain] = useState<string>('');
  const [toChain, setToChain] = useState<string>('');
  const [selectedToken, setSelectedToken] = useState<TokenType>('USDC');
  const [amount, setAmount] = useState<string>('');
  const [recipient, setRecipient] = useState<string>('');
  const [allowance, setAllowance] = useState<string>('0');
  const [balance, setBalance] = useState<string>('0');
  const [fee, setFee] = useState<string>('0');
  const [estimatedReceiveAmount, setEstimatedReceiveAmount] = useState<string>('0');
  const [bridgeFee, setBridgeFee] = useState<string>('0');
  const [feeBreakdown, setFeeBreakdown] = useState<{
    transportFee: string;
    serviceFee: string;
    serviceFeePercentage: number;
  } | null>(null);
  const [transactions, setTransactions] = useState<BridgeTransaction[]>([]);
  const [transactionState, setTransactionState] = useState<TransactionState>({
    isLoading: false,
    error: null,
    success: false,
    hash: null,
  });

  // Load transactions from local storage - only on initial mount
  useEffect(() => {
    if (!walletAddress) return;
    
    try {
      const storedTransactions = localStorage.getItem(`${LOCAL_STORAGE_KEY}-${walletAddress}`);
      if (storedTransactions) {
        const parsedTransactions = JSON.parse(storedTransactions);
        setTransactions(parsedTransactions);
      }
    } catch (error) {
      console.error('Error loading transactions from local storage:', error);
    }
  }, [walletAddress]); // Only run when wallet address changes

  // We'll handle localStorage updates manually in our functions instead of using this effect
  // This prevents race conditions and ensures immediate updates

  // Check pending transaction statuses
  useEffect(() => {
    if (!transactions.length) return;
    
    const checkPendingTransactions = async () => {
      // Check both pending and processing transactions
      const pendingTransactions = transactions.filter(tx => 
        tx.status === 'pending' || tx.status === 'processing'
      );
      
      if (!pendingTransactions.length) return;
      
      const updatedTransactions = [...transactions];
      let hasUpdates = false;
      
      // Process each transaction independently
      for (const tx of pendingTransactions) {
        try {
          const chainId = CHAIN_CONFIGS[tx.fromChain]?.chainId;
          if (!chainId) {
            continue;
          }
          
          // Get the current index of this transaction in the updatedTransactions array
          const txIndex = updatedTransactions.findIndex(t => t.hash === tx.hash);
          if (txIndex === -1) continue; // Skip if transaction not found
          
          // Always check the cross-chain message status first
          const crossChainResult = await checkCrossChainMessageStatus(tx.hash);
          
          // If the cross-chain status is completed or failed, use that
          if (crossChainResult.status === 'completed' || crossChainResult.status === 'failed') {
            updatedTransactions[txIndex] = { 
              ...updatedTransactions[txIndex], 
              status: crossChainResult.status,
              toChainTxHash: crossChainResult.toChainTxHash
            };
            hasUpdates = true;
            continue;
          }
          
          // If the cross-chain API returns "processing", update immediately
          if (crossChainResult.status === 'processing' && updatedTransactions[txIndex].status !== 'processing') {
            updatedTransactions[txIndex] = { ...updatedTransactions[txIndex], status: 'processing' };
            hasUpdates = true;
            continue;
          }
          
          // If cross-chain status is still pending, check the source chain
          const sourceStatus = await checkTransactionStatus(tx.hash, chainId);
          
          if (sourceStatus === 'failed') {
            // If the source transaction failed, mark the whole bridge transaction as failed
            updatedTransactions[txIndex] = { ...updatedTransactions[txIndex], status: 'failed' };
            hasUpdates = true;
            continue;
          }
          
          if (sourceStatus === 'completed' && updatedTransactions[txIndex].status === 'pending') {
            // If the source transaction is completed but cross-chain is not, mark as processing
            updatedTransactions[txIndex] = { ...updatedTransactions[txIndex], status: 'processing' };
            hasUpdates = true;
          }
        } catch (error) {
          console.error(`Error checking status for transaction ${tx.hash}:`, error);
        }
      }
      
      if (hasUpdates) {
        setTransactions(updatedTransactions);
        
        // Also update localStorage
        if (walletAddress) {
          try {
            localStorage.setItem(`${LOCAL_STORAGE_KEY}-${walletAddress}`, JSON.stringify(updatedTransactions));
          } catch (error) {
            console.error('Error saving updated transactions to local storage:', error);
          }
        }
      }
    };
    
    // Check immediately
    checkPendingTransactions();
    
    // Then check more frequently (every 10 seconds)
    const interval = setInterval(checkPendingTransactions, 10000);
    
    return () => clearInterval(interval);
  }, [transactions, walletAddress]); // Add walletAddress as a dependency

  // Set recipient to wallet address by default
  useEffect(() => {
    if (walletAddress && !recipient) {
      setRecipient(walletAddress);
    }
  }, [walletAddress, recipient]);

  // Check allowance when necessary values change
  useEffect(() => {
    const checkTokenAllowance = async () => {
      if (!fromChain || !selectedToken || !walletAddress) {
        setAllowance('0');
        return;
      }

      try {
        const tokenAddress = CHAIN_CONFIGS[fromChain][selectedToken];
        if (!tokenAddress) {
          setAllowance('0');
          return;
        }

        const bridgeAddress = CHAIN_CONFIGS[fromChain].bridge;
        
        // Use Wagmi function instead of provider-based function
        const allowanceAmount = await checkAllowanceWagmi(
          tokenAddress,
          walletAddress,
          bridgeAddress
        );

        setAllowance(allowanceAmount);
      } catch (error) {
        console.error('Error checking allowance:', error);
        setAllowance('0');
      }
    };

    checkTokenAllowance();
  }, [fromChain, selectedToken, walletAddress]);

  // Get token balance when chain or token changes
  useEffect(() => {
    const fetchTokenBalance = async () => {
      if (!fromChain || !selectedToken || !walletAddress) {
        setBalance('0');
        return;
      }

      try {
        const tokenAddress = CHAIN_CONFIGS[fromChain][selectedToken];
        if (!tokenAddress) {
          setBalance('0');
          return;
        }

        // Use Wagmi function instead of provider-based function
        const balanceAmount = await getTokenBalanceWagmi(
          tokenAddress,
          walletAddress
        );

        setBalance(balanceAmount);
      } catch (error) {
        console.error('Error getting token balance:', error);
        setBalance('0');
      }
    };

    fetchTokenBalance();
  }, [fromChain, selectedToken, walletAddress]);

  // Estimate fee when from/to chains change
  useEffect(() => {
    const estimateFee = async () => {
      if (!fromChain || !toChain) {
        setFee('0');
        setBridgeFee('0');
        return;
      }

      try {
        const provider = await getProvider(CHAIN_CONFIGS[fromChain].chainId);
        if (!provider) throw new Error('Failed to initialize provider');

        const feeAmount = await estimateBridgeFee(
          CHAIN_CONFIGS[fromChain],
          CHAIN_CONFIGS[toChain],
          provider
        );

        setFee(feeAmount);

        // Get bridge fee from Fee API
        if (recipient && amount && parseFloat(amount) > 0) {
          try {
            const requestBody = {
              toChainId: CHAIN_CONFIGS[toChain].bip44ChainId,
              gasLimit: 200000,
              userAddress: recipient,
              feeToken: CHAIN_CONFIGS[fromChain][selectedToken],
              bridgeAmount: amount, // Include bridge amount for service fee calculation
            };

            const feeResponse = await fetch('https://gateway-api.shido.io/api/fee/estimate', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody),
            });

            if (feeResponse.ok) {
              const feeResult = await feeResponse.json();
              if (feeResult.success) {
                setBridgeFee(feeResult.data.fee);
                setFeeBreakdown(feeResult.data.feeBreakdown || null);
              }
            }
          } catch (error) {
            console.error('Error getting bridge fee estimate:', error);
            setBridgeFee('0');
            setFeeBreakdown(null);
          }
        } else {
          setBridgeFee('0');
          setFeeBreakdown(null);
        }
      } catch (error) {
        console.error('Error estimating fee:', error);
        setFee('0');
        setBridgeFee('0');
      }
    };

    estimateFee();
  }, [fromChain, toChain, recipient, selectedToken, amount]);

  // Calculate estimated receive amount when amount or bridge fee changes
  useEffect(() => {
    if (!amount || !bridgeFee) {
      setEstimatedReceiveAmount('0');
      return;
    }

    try {
      const amountNum = parseFloat(amount);
      const feeNum = parseFloat(bridgeFee);
      
      if (amountNum > feeNum) {
        const receiveAmount = amountNum - feeNum;
        setEstimatedReceiveAmount(receiveAmount.toFixed(6));
      } else {
        setEstimatedReceiveAmount('0');
      }
    } catch (error) {
      console.error('Error calculating estimated receive amount:', error);
      setEstimatedReceiveAmount('0');
    }
  }, [amount, bridgeFee]);

  // Handle from chain change
  const handleFromChainChange = useCallback((chainId: string) => {
    setFromChain(chainId);
    
    // If from chain is Shido, to chain can be any other chain
    // If from chain is not Shido, to chain can only be Shido
    if (chainId === 'shido-testnet') {
      if (toChain === 'shido-testnet') {
        setToChain('');
      }
    } else {
      setToChain('shido-testnet');
    }
  }, [toChain]);

  // Handle to chain change
  const handleToChainChange = (chainId: string) => {
    setToChain(chainId);
  };

  // Handle token change
  const handleTokenChange = (token: TokenType) => {
    setSelectedToken(token);
  };

  // Handle amount change
  const handleAmountChange = (value: string) => {
    setAmount(value);
  };

  // Handle recipient change
  const handleRecipientChange = (address: string) => {
    setRecipient(address);
  };

  // Approve token
  const handleApproveToken = async () => {
    if (!fromChain || !selectedToken || !amount || !walletAddress) {
      setTransactionState({
        isLoading: false,
        error: 'Missing required fields',
        success: false,
        hash: null,
      });
      return;
    }

    setTransactionState({
      isLoading: true,
      error: null,
      success: false,
      hash: null,
    });

    try {
      const tokenAddress = CHAIN_CONFIGS[fromChain][selectedToken];
      if (!tokenAddress) {
        throw new Error(`${selectedToken} not supported on ${CHAIN_CONFIGS[fromChain].name}`);
      }

      const bridgeAddress = CHAIN_CONFIGS[fromChain].bridge;
      
      // Use Wagmi function instead of provider-based function
      const txHash = await approveTokenWagmi(
        tokenAddress,
        bridgeAddress,
        amount
      );

      // Update allowance using Wagmi function
      const newAllowance = await checkAllowanceWagmi(
        tokenAddress,
        walletAddress,
        bridgeAddress
      );
      setAllowance(newAllowance);

      setTransactionState({
        isLoading: false,
        error: null,
        success: true,
        hash: txHash,
      });
    } catch (error: any) {
      console.error('Error approving token:', error);
      setTransactionState({
        isLoading: false,
        error: error.message || 'Failed to approve token',
        success: false,
        hash: null,
      });
    }
  };

  // Bridge token - This function adds a new transaction to the history
  const handleBridgeToken = async () => {
    if (!fromChain || !toChain || !selectedToken || !amount || !recipient) {
      setTransactionState({
        isLoading: false,
        error: 'Missing required fields',
        success: false,
        hash: null,
      });
      return;
    }

    setTransactionState({
      isLoading: true,
      error: null,
      success: false,
      hash: null,
    });

    try {
      const provider = await getProvider(CHAIN_CONFIGS[fromChain].chainId);
      if (!provider) throw new Error('Failed to initialize provider');

      // Get fee data from the Fee API
      let feeData;
      try {
        const requestBody = {
          toChainId: CHAIN_CONFIGS[toChain].bip44ChainId,
          gasLimit: 200000,
          userAddress: recipient,
          feeToken: CHAIN_CONFIGS[fromChain][selectedToken], // Use the token address as fee token
          bridgeAmount: amount, // Include bridge amount for service fee calculation
        };

        console.log('Fee API request:', requestBody);

        const feeResponse = await fetch('https://gateway-api.shido.io/api/fee/estimate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        console.log('Fee API response status:', feeResponse.status);

        if (!feeResponse.ok) {
          const errorText = await feeResponse.text();
          console.error('Fee API error response:', errorText);
          throw new Error(`Fee API returned ${feeResponse.status}: ${errorText}`);
        }

        const feeResult = await feeResponse.json();
        console.log('Fee API result:', feeResult);

        if (!feeResult.success) {
          throw new Error(feeResult.error || 'Failed to get fee estimate');
        }

        feeData = {
          fee: feeResult.data.fee,
          deadline: feeResult.data.deadline,
          signature: feeResult.data.signature,
        };

        console.log('Fee data prepared:', feeData);
      } catch (feeError) {
        console.error('Error getting fee estimate:', feeError);
        throw new Error(`Failed to get fee estimate: ${feeError.message}`);
      }

      // Use Wagmi function instead of provider-based function
      const txHash = await bridgeTokenWagmi(
        CHAIN_CONFIGS[fromChain],
        CHAIN_CONFIGS[toChain],
        selectedToken,
        amount,
        recipient,
        feeData
      );

      // Add transaction to history
      const newTransaction: BridgeTransaction = {
        hash: txHash,
        fromChain,
        toChain,
        token: selectedToken,
        amount,
        status: 'pending',
        timestamp: Date.now(),
        recipient,
      };
      
      // Update transactions state immediately
      const updatedTransactions = [newTransaction, ...transactions];
      
      // Force a re-render by using setTimeout
      setTimeout(() => {
        setTransactions(updatedTransactions);
        
        // Also update localStorage immediately
        if (walletAddress) {
          try {
            localStorage.setItem(`${LOCAL_STORAGE_KEY}-${walletAddress}`, JSON.stringify(updatedTransactions));
          } catch (error) {
            console.error('Error saving transactions to local storage:', error);
          }
        }
      }, 0);

      setTransactionState({
        isLoading: false,
        error: null,
        success: true,
        hash: txHash,
      });

      // Reset form
      setAmount('');
    } catch (error: any) {
      console.error('Error bridging token:', error);
      setTransactionState({
        isLoading: false,
        error: error.message || 'Failed to bridge token',
        success: false,
        hash: null,
      });
    }
  };

  // Check if approval is needed
  const needsApproval = () => {
    if (!amount || !allowance || !fromChain) return false;
    
    try {
      // Get the correct decimals for the source chain
      let decimals;
      if (fromChain === 'bsc-testnet') {
        decimals = 18; // BSC USDC has 18 decimals
      } else {
        decimals = 6;  // Other chains USDC has 6 decimals
      }
      
      const amountBN = ethers.utils.parseUnits(amount, decimals);
      const allowanceBN = ethers.utils.parseUnits(allowance, decimals);
      
      return amountBN.gt(allowanceBN);
    } catch (error) {
      console.error('Error checking if approval is needed:', error);
      return true;
    }
  };

  // Clear transaction history
  const clearTransactions = useCallback(() => {
    // Clear state
    setTransactions([]);
    
    // Clear localStorage
    if (walletAddress) {
      try {
        localStorage.removeItem(`${LOCAL_STORAGE_KEY}-${walletAddress}`);
      } catch (error) {
        console.error('Error removing transactions from local storage:', error);
      }
    }
  }, [walletAddress]);

  return {
    fromChain,
    toChain,
    selectedToken,
    amount,
    recipient,
    allowance,
    balance,
    fee,
    estimatedReceiveAmount,
    bridgeFee,
    feeBreakdown,
    transactions,
    transactionState,
    handleFromChainChange,
    handleToChainChange,
    handleTokenChange,
    handleAmountChange,
    handleRecipientChange,
    handleApproveToken,
    handleBridgeToken,
    needsApproval,
    clearTransactions,
  };
};
